import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import TemplatePickerCard from "../../components/whatsapp/templates/TemplatePickerCard";
import {
  listTemplates,
  getWhatsAppErrorMessage,
  unwrapWhatsAppList,
} from "../../services/whatsappApi";
import {
  CAMPAIGN_TEMPLATE_STATUS_OPTIONS,
  getCampaignTemplateLanguage,
  getCampaignTemplateName,
} from "../../utils/whatsappCampaign";
import { parseApiPagination } from "../../utils/parseApiPagination";
import "./whatsapp.css";
import "./whatsapp-campaigns-list.css";
import "./whatsapp-campaign.css";
import WhatsAppPageShell from "./WhatsAppPageShell";

const PAGE_SIZE = 10;

function reportError(err, fallback) {
  const msg = getWhatsAppErrorMessage(err, fallback);
  toast.error(msg);
  return msg;
}

export default function WhatsAppTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listTemplates({
        status: statusFilter,
        limit: PAGE_SIZE,
        page,
        search: searchQuery,
      });
      const data = unwrapWhatsAppList(res);
      setTemplates(data);
      const pagination = parseApiPagination(res, data.length);
      const apiGaveTotal =
        res?.total != null ||
        res?.data?.total != null ||
        res?.pagination?.total != null ||
        res?.data?.pagination?.total != null;
      const resolvedTotal = apiGaveTotal ? pagination.total : data.length;
      setTotal(resolvedTotal);
      const fullPage = data.length === PAGE_SIZE;
      if (apiGaveTotal) {
        const pages = Math.max(
          1,
          pagination.total_pages || Math.ceil(resolvedTotal / PAGE_SIZE),
        );
        setTotalPages(pages);
        setHasNextPage(page < pages);
      } else {
        setHasNextPage(fullPage);
        setTotalPages(fullPage ? page + 1 : page);
      }
    } catch (err) {
      const msg = reportError(err, "Failed to load templates");
      setError(msg);
      setTemplates([]);
      setTotal(0);
      setHasNextPage(false);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const showPagination = !loading && !error && templates.length > 0;
  const pageStart = templates.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = (page - 1) * PAGE_SIZE + templates.length;

  return (
    <WhatsAppPageShell className="whatsapp-page-shell--panel">
      <div className="whatsapp-inbox-root">
        <div className="whatsapp-page-panel wa-campaigns-list-page">
            <header className="whatsapp-page-header">
              <div>
                <h1>My Template</h1>
                <p className="whatsapp-page-subtitle">
                  WhatsApp message templates for your campaigns
                </p>
              </div>
              <button
                type="button"
                className="whatsapp-btn-primary"
                onClick={() => navigate("/whatsapp/templates/create")}
              >
                <Icon icon="solar:add-circle-line-duotone" width={18} />
                Add Template
              </button>
            </header>

            <div className="wa-page-toolbar">
              <div className="wa-page-toolbar-search">
                <input
                  type="search"
                  placeholder="Search templates..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  aria-label="Search templates"
                />
                <button
                  type="button"
                  className="wa-page-toolbar-search-btn"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </div>

              <div className="wa-page-toolbar-actions">
                <label className="wa-page-toolbar-filter">
                  <Icon icon="solar:filter-line-duotone" width={16} />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Filter by status"
                  >
                    {CAMPAIGN_TEMPLATE_STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.value ? opt.label : "All statuses"}
                      </option>
                    ))}
                  </select>
                </label>

                <button
                  type="button"
                  className="wa-page-toolbar-btn-outline"
                  onClick={fetchTemplates}
                  disabled={loading}
                >
                  <Icon icon="solar:refresh-linear" width={16} />
                  Refresh
                </button>
              </div>
            </div>

            <div className="wa-campaigns-list-body">
              {loading ? (
                <div className="wa-campaign-template-grid">
                  {[1, 2, 3, 4, 5, 6].map((k) => (
                    <div key={k} className="wa-campaign-tpl-card skeleton" />
                  ))}
                </div>
              ) : error ? (
                <div className="whatsapp-list-error">{error}</div>
              ) : templates.length === 0 ? (
                <div className="whatsapp-empty-state">
                  <Icon
                    icon="solar:document-text-line-duotone"
                    width={56}
                    style={{ opacity: 0.45 }}
                  />
                  <p>
                    No templates found. Add a template or try another search.
                  </p>
                </div>
              ) : (
                <div className="wa-campaign-template-grid">
                  {templates.map((tpl) => {
                    const key = `${getCampaignTemplateName(tpl)}-${getCampaignTemplateLanguage(tpl)}`;
                    return <TemplatePickerCard key={key} template={tpl} />;
                  })}
                </div>
              )}
            </div>

            {showPagination ? (
              <footer className="wa-campaigns-list-footer">
                <span className="wa-campaigns-list-footer-count">
                  Showing {templates.length}
                  {total > templates.length ? ` of ${total}` : ""} templates
                </span>
                <div className="wa-campaigns-list-footer-nav">
                  <span className="wa-campaigns-list-range">
                    {pageStart}-{pageEnd}
                    {total > templates.length ? ` of ${total}` : ""}
                  </span>
                  <div className="wa-campaigns-list-page-nav">
                    <button
                      type="button"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      aria-label="Previous page"
                    >
                      <Icon icon="solar:alt-arrow-left-linear" width={16} />
                    </button>
                    <span>
                      Page {page}
                      {totalPages > 1 ? ` of ${totalPages}` : ""}
                    </span>
                    <button
                      type="button"
                      disabled={!hasNextPage || loading}
                      onClick={() => setPage((p) => p + 1)}
                      aria-label="Next page"
                    >
                      <Icon icon="solar:alt-arrow-right-linear" width={16} />
                    </button>
                  </div>
                </div>
              </footer>
            ) : null}
        </div>
      </div>
    </WhatsAppPageShell>
  );
}
